import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { OrdersFilterBar } from '../components/orders/OrdersFilterBar';
import {
  fetchOrderList,
  searchOrders,
  type OrderListItem,
  type OrderListFilters,
  type OrderRecordTypeOption,
} from '../lib/orders';
import {
  BRAND_COLORS,
  ORDER_STATUS_COLORS,
  ORDER_BRAND_OPTIONS,
  ORDER_STATUS_OPTIONS,
  ORDER_PERIOD_OPTIONS,
  DEFAULT_ORDER_PERIOD,
} from '../lib/order-constants';
import { formatCurrency, formatRelativeTime } from '../lib/format';
import { KlachtBadge } from '../components/klachten/KlachtBadge';
import { VervolgWoBadge } from '../components/klachten/VervolgWoBadge';

const ALLOWED_DAYS = new Set(['30', '90', '365', 'all']);

function readDays(value: string | null): OrderListFilters['days'] {
  if (value && ALLOWED_DAYS.has(value)) return value as OrderListFilters['days'];
  return DEFAULT_ORDER_PERIOD as OrderListFilters['days'];
}

function labelFor(
  options: Array<{ value: string; label: string }>,
  value: string
): string | null {
  const match = options.find((o) => o.value === value);
  return match?.label ?? null;
}

export function OrdersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const brand = searchParams.get('brand') ?? '';
  const status = searchParams.get('status') ?? '';
  const recordType = searchParams.get('recordType') ?? '';
  const days = readDays(searchParams.get('days'));
  const reminderActieVereist = searchParams.get('reminderActieVereist') === 'true';
  const telefoonOntbreekt = searchParams.get('telefoonOntbreekt') === 'true';

  const [items, setItems] = useState<OrderListItem[]>([]);
  const [availableRecordTypes, setAvailableRecordTypes] = useState<OrderRecordTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<OrderListItem[] | null>(null);

  // Load list whenever filters change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchOrderList({
      brand: brand || undefined,
      status: status || undefined,
      recordType: recordType || undefined,
      days,
      reminderActieVereist: reminderActieVereist || undefined,
      telefoonOntbreekt: telefoonOntbreekt || undefined,
    })
      .then((response) => {
        if (!cancelled) {
          setItems(response.orders);
          setAvailableRecordTypes(response.availableRecordTypes);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message || 'Kon orders niet laden');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [brand, status, recordType, days, reminderActieVereist, telefoonOntbreekt]);

  // Search debounce
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }

    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(() => {
      searchOrders(trimmed, controller.signal)
        .then((results) => {
          setSearchResults(results);
          setSearching(false);
        })
        .catch((err: Error & { name?: string }) => {
          if (err.name === 'AbortError') return;
          setSearching(false);
          setSearchResults([]);
        });
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const isSearching = query.trim().length >= 2;
  const displayItems = isSearching ? (searchResults ?? []) : items;

  const handleFilterChange = (patch: {
    brand?: string;
    status?: string;
    recordType?: string;
    days?: string;
    reminderActieVereist?: boolean;
    telefoonOntbreekt?: boolean;
  }) => {
    setSearchParams(
      (sp) => {
        if (patch.brand !== undefined) {
          if (patch.brand) sp.set('brand', patch.brand);
          else sp.delete('brand');
        }
        if (patch.status !== undefined) {
          if (patch.status) sp.set('status', patch.status);
          else sp.delete('status');
        }
        if (patch.recordType !== undefined) {
          if (patch.recordType) sp.set('recordType', patch.recordType);
          else sp.delete('recordType');
        }
        if (patch.days !== undefined) {
          if (patch.days && patch.days !== DEFAULT_ORDER_PERIOD) sp.set('days', patch.days);
          else sp.delete('days');
        }
        if (patch.reminderActieVereist !== undefined) {
          if (patch.reminderActieVereist) sp.set('reminderActieVereist', 'true');
          else sp.delete('reminderActieVereist');
        }
        if (patch.telefoonOntbreekt !== undefined) {
          if (patch.telefoonOntbreekt) sp.set('telefoonOntbreekt', 'true');
          else sp.delete('telefoonOntbreekt');
        }
        return sp;
      },
      { replace: true }
    );
  };

  const handleClearFilters = () => {
    setSearchParams(
      (sp) => {
        sp.delete('brand');
        sp.delete('status');
        sp.delete('recordType');
        sp.delete('days');
        sp.delete('reminderActieVereist');
        return sp;
      },
      { replace: true }
    );
  };

  const subtitle = useMemo(() => {
    if (isSearching) return 'Zoekt in alle orders';

    const parts: string[] = [`${items.length} orders`];
    if (brand) parts.push(labelFor(ORDER_BRAND_OPTIONS, brand) ?? brand);
    if (recordType) {
      const rt = availableRecordTypes.find((r) => r.developerName === recordType);
      parts.push(rt?.name ?? recordType);
    }
    if (status) parts.push(labelFor(ORDER_STATUS_OPTIONS, status) ?? status);
    parts.push(labelFor(ORDER_PERIOD_OPTIONS, days ?? DEFAULT_ORDER_PERIOD) ?? '');

    let text = parts.filter(Boolean).join(' · ');
    if (items.length === 200 && !status) {
      text += ' (max bereikt — verfijn filter)';
    }
    return text;
  }, [isSearching, items.length, brand, status, recordType, days, availableRecordTypes]);

  return (
    <>
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Orders</h1>
              <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
            </div>
            <div className="relative flex-1 max-w-sm">
              <svg
                className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Zoek ordernr, klant, email, postcode..."
                className="w-full pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-3.5 h-3.5 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-b border-slate-200 bg-white">
          <OrdersFilterBar
            brand={brand}
            status={status}
            recordType={recordType}
            days={days ?? DEFAULT_ORDER_PERIOD}
            reminderActieVereist={reminderActieVereist}
            telefoonOntbreekt={telefoonOntbreekt}
            availableRecordTypes={availableRecordTypes}
            onChange={handleFilterChange}
            onClear={handleClearFilters}
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 space-y-2 animate-pulse">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-12 bg-slate-100 rounded" />
              ))}
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            </div>
          ) : !isSearching && items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400 gap-2">
              <p className="text-sm">Geen orders voor deze filters</p>
              <button
                onClick={handleClearFilters}
                className="text-xs text-indigo-600 hover:underline"
              >
                Filters wissen
              </button>
            </div>
          ) : isSearching && !searching && displayItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400 gap-2">
              <p className="text-sm">Geen orders gevonden voor "{query}"</p>
              <button
                onClick={() => setQuery('')}
                className="text-xs text-indigo-600 hover:underline"
              >
                Zoekopdracht wissen
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase tracking-wide bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left font-medium px-6 py-2">Ordernr</th>
                  <th className="text-left font-medium px-4 py-2">Klant</th>
                  <th className="text-left font-medium px-4 py-2">Merk</th>
                  <th className="text-left font-medium px-4 py-2">Plaats</th>
                  <th className="text-right font-medium px-4 py-2">Totaal</th>
                  <th className="text-left font-medium px-4 py-2">Status</th>
                  <th className="text-left font-medium px-4 py-2">Leeftijd</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayItems.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/orders/${row.id}`)}
                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-6 py-3 font-medium text-slate-900">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span>{row.orderNumber || '—'}</span>
                        {row.klachtParentId && row.klachtParentNummer && (
                          <KlachtBadge
                            klachtId={row.klachtParentId}
                            klachtNummer={row.klachtParentNummer}
                            status={row.klachtStatus ?? undefined}
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        {row.isVervolgWo && (
                          <VervolgWoBadge
                            klachtId={row.klachtParentId ?? undefined}
                            klachtNummer={row.klachtParentNummer ?? undefined}
                            size="sm"
                          />
                        )}
                        {row.reminderState?.inSequence &&
                          (row.reminderState.bounced || row.reminderState.count >= 3) && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-red-100 text-red-700 border border-red-200"
                            title={
                              row.reminderState.bounced
                                ? 'Email-bounce gedetecteerd — bel klant'
                                : `${row.reminderState.count} reminders verstuurd zonder reactie`
                            }
                          >
                            {row.reminderState.bounced ? '✉︎ bounce' : `${row.reminderState.count}× reminder`}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{row.customerName}</td>
                    <td className="px-4 py-3">
                      {row.brand ? (
                        <span
                          className={clsx(
                            'text-[10px] px-1.5 py-0.5 rounded font-medium',
                            BRAND_COLORS[row.brand] || 'bg-slate-100 text-slate-600'
                          )}
                        >
                          {row.brand}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {[row.postcode, row.city].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-900">
                      {row.totalInclBtw > 0 ? formatCurrency(row.totalInclBtw) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={clsx(
                          'text-[10px] px-1.5 py-0.5 rounded font-medium',
                          ORDER_STATUS_COLORS[row.status] || 'bg-slate-100 text-slate-600'
                        )}
                      >
                        {row.status || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {formatRelativeTime(row.createdDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
